import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface Order {
  id: string
  symbol: string
  type: 'market' | 'limit' | 'stop_loss'
  side: 'buy' | 'sell'
  quantity: number
  price?: number
  stopPrice?: number
  status: 'pending' | 'filled' | 'cancelled' | 'partial'
  filledQuantity: number
  createdAt: string
  updatedAt: string
}

interface TradingState {
  orders: Order[]
  activeOrders: Order[]
  orderHistory: Order[]
  loading: boolean
  error: string | null
}

const initialState: TradingState = {
  orders: [],
  activeOrders: [],
  orderHistory: [],
  loading: false,
  error: null,
}

const tradingSlice = createSlice({
  name: 'trading',
  initialState,
  reducers: {
    createOrderStart: (state) => {
      state.loading = true
      state.error = null
    },
    createOrderSuccess: (state, action: PayloadAction<Order>) => {
      state.loading = false
      state.orders.push(action.payload)
      state.activeOrders.push(action.payload)
      state.error = null
    },
    createOrderFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
    updateOrder: (state, action: PayloadAction<Order>) => {
      const index = state.orders.findIndex(order => order.id === action.payload.id)
      if (index !== -1) {
        state.orders[index] = action.payload
        
        // Update active orders
        const activeIndex = state.activeOrders.findIndex(order => order.id === action.payload.id)
        if (action.payload.status === 'filled' || action.payload.status === 'cancelled') {
          if (activeIndex !== -1) {
            state.activeOrders.splice(activeIndex, 1)
          }
          state.orderHistory.push(action.payload)
        } else if (activeIndex !== -1) {
          state.activeOrders[activeIndex] = action.payload
        }
      }
    },
    cancelOrder: (state, action: PayloadAction<string>) => {
      const order = state.orders.find(o => o.id === action.payload)
      if (order) {
        order.status = 'cancelled'
        order.updatedAt = new Date().toISOString()
        
        // Remove from active orders
        const activeIndex = state.activeOrders.findIndex(o => o.id === action.payload)
        if (activeIndex !== -1) {
          state.activeOrders.splice(activeIndex, 1)
        }
        
        // Add to history
        state.orderHistory.push(order)
      }
    },
    clearError: (state) => {
      state.error = null
    },
  },
})

export const {
  createOrderStart,
  createOrderSuccess,
  createOrderFailure,
  updateOrder,
  cancelOrder,
  clearError,
} = tradingSlice.actions

export default tradingSlice.reducer